import { Question } from '../types';
import { QuizItem } from '@/store/assessment-store';
import { getQuestionType } from './questionUtils';

// Correctness helpers; will be updated once the API returns per-question correctness data.

/**
 * Checks if an option matches the correct answer. Handles correct as
 * string, string[], boolean, or number (index into allOptions).
 */
export function optionMatchesCorrect(
  option: string | boolean,
  correct: string | string[] | boolean | number,
  allOptions: (string | boolean)[]
): boolean {
  // API may return an index (0, 1, 2, ...) instead of the value itself
  let resolvedCorrect: string | string[] | boolean = correct as string | string[] | boolean;
  if (typeof correct === 'number' && Array.isArray(allOptions)) {
    const indexed = allOptions[correct];
    if (indexed !== undefined) resolvedCorrect = indexed;
  }

  if (typeof resolvedCorrect === 'boolean') {
    const normOption =
      typeof option === 'string' && (option === 'true' || option === 'false')
        ? option === 'true'
        : option;
    return normOption === resolvedCorrect;
  }

  if (Array.isArray(resolvedCorrect)) {
    return resolvedCorrect.some((c) => {
      if (typeof option === 'boolean' && typeof c === 'boolean') return option === c;
      if (typeof option === 'boolean') return (option ? 'true' : 'false') === String(c).trim();
      return String(option).trim() === String(c).trim();
    });
  }

  // String comparison
  if (typeof option === 'boolean') {
    return (option ? 'true' : 'false') === String(resolvedCorrect).trim();
  }
  return String(option).trim() === String(resolvedCorrect).trim();
}

/**
 * Whether a specific option is correct for a question.
 * TODO: currently treats any selected option as correct — replace with real
 * correct-answer comparison once the API provides it.
 */
export function getOptionCorrectness(
  question: Question,
  option: string | boolean,
  selectedOptions: string | string[] | boolean | undefined
): boolean | null {
  if (!selectedOptions) return null; // not answered

  const questionType = getQuestionType(question);

  if (questionType === 'multiple_choice_question') {
    if (!Array.isArray(selectedOptions)) return null;
    const isSelected = selectedOptions.includes(option as string);
    return isSelected ? true : null;
  } else if (questionType === 'true_or_false_question') {
    const isSelected = selectedOptions === option;
    return isSelected ? true : null;
  } else {
    // single choice question
    const isSelected = selectedOptions === option;
    return isSelected ? true : null;
  }
}

/**
 * Whether a question was answered correctly overall.
 * TODO: placeholder — always true once answered; replace with per-question
 * correctness from the API response.
 */
export function getQuestionCorrectness(
  question: Question,
  quiz: QuizItem[]
): boolean | null {
  const quizArray = Array.isArray(quiz) ? quiz : [];
  const quizItem = quizArray.find((q) => q.question_uid === question.metadata_uid);
  if (!quizItem || !quizItem.selected_options) return null; // not answered
  return true;
}

